import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
Container,
Box,
Typography,
TextField,
Button,
Card,
CardContent,
CardHeader,
Divider,
Grid,
Paper,
Alert,
LinearProgress,
CircularProgress,
Fade,
Slide,
Grow,
Tabs,
AlertTitle,
Tab,
IconButton,
Tooltip,
useMediaQuery,
useTheme,
Stepper,
Step,
StepLabel,
StepContent
} from '@mui/material';
import {
Add as AddIcon,
Remove as RemoveIcon,
CheckCircle as CheckCircleIcon,
Error as ErrorIcon,
ExpandMore as ExpandMoreIcon,
Article as ArticleIcon,
Edit as EditIcon,
Science as ScienceIcon,
Factory as FactoryIcon,
Verified as VerifiedIcon,
Description as DescriptionIcon,
ArrowForward as ArrowForwardIcon,
TramRounded
} from '@mui/icons-material';
import { APIProductStore, APIProductUpdate, APIResearcherList } from '../../utilities/APIS';
import { getTodaysDate } from '../../utilities/Functions';
import GuestUser from '../main/GuestUser';
import LockedTab from '../main/LockedTab';
import NavigationMenu from '../navigation/NavigationMenu';
import { headers } from '../../utilities/Env';
import useCustomToast from '../../hooks/useToast';
import { TEXT_CONNECTION_ERROR } from '../../utilities/Text';
import LoadingState from '../../components/LoadingState';

const SubmitProduct = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const showToast = useCustomToast();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [postStatus, setPostStatus] = useState('ADD'); // 'ADD' or 'UPDATE'
    const [editingProductId, setEditingProductId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [success, setSuccess] = useState(false);

    const [state, setState] = useState({
        userLoggedIn: false,
        userName: '',
        userId: '',
        userUniversity: '',
        ProductTitle: '',
        ProductDescription: '',
        ProductDetailsInputs: [{ data: '' }],
        composition: '',
        manufacturer: '',
        tradeMark: '',
        patent: '',
        showSummaryPreviewTab: false,
        showSubmitTab: false,
        activeTab: 0,
    });

    // Steps for guided submission
    const steps = [
        { label: 'Basic Information', icon: <DescriptionIcon /> },
        { label: 'Product Details', icon: <ScienceIcon /> },
        { label: 'Technical Specifications', icon: <VerifiedIcon /> },
        { label: 'Review & Submit', icon: <CheckCircleIcon /> }
    ];

    useEffect(() => {
        loadUser();
        checkEditMode();
    }, []);

    // Check if we're in edit mode and load product data
    const checkEditMode = () => {
        if (location.state) {
            const productData = location.state;
            console.log('Edit mode detected with data:', productData);
            
            setPostStatus('UPDATE');
            
            if (productData.productId) {
                setEditingProductId(productData.productId);
            }

            // Populate form with existing product data
            setState(prev => ({
                ...prev,
                ProductTitle: productData.productTitle || '',
                ProductDescription: productData.productDescription || '',
                ProductDetailsInputs: productData.productDetails ? JSON.parse(productData.productDetails) : [{ data: '' }],
                composition: productData.composition || '',
                manufacturer: productData.manufacturer || '',
                tradeMark: productData.tradeMark || '',
                patent: productData.patent || '',
                showSummaryPreviewTab: true,
                showSubmitTab: true
            }));

            showToast("Edit Mode", "You are now editing an existing product", "info");
        }
    };

    const loadUser = () => {
        const user = sessionStorage.getItem('reseacher');
        if (user) {
            const jsonData = JSON.parse(user);
            setState(prev => ({
                ...prev,
                userName: jsonData.name,
                userLoggedIn: jsonData.loggedIn,
                userId: jsonData.id,
                userUniversity: jsonData.university
            }));
        }
    };

    const handleTabChange = (event, newValue) => {
        setState(prev => ({ ...prev, activeTab: newValue }));
    };

    const handleNextStep = () => {
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handlePrevStep = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setState(prev => ({ ...prev, [name]: value }));
        
        // Show preview tab if title is entered
        if (name === 'ProductTitle' && value.length > 0) {
            setState(prev => ({
                ...prev,
                showSummaryPreviewTab: true,
                showSubmitTab: true
            }));
        }
    };

    const handleArrayInputChange = (index, value) => {
        const updatedArray = [...state.ProductDetailsInputs];
        updatedArray[index].data = value;
        setState(prev => ({ ...prev, ProductDetailsInputs: updatedArray }));
    };

    const addInputField = () => {
        setState(prev => ({ 
            ...prev, 
            ProductDetailsInputs: [...prev.ProductDetailsInputs, { data: '' }] 
        }));
    };

    const removeInputField = (index) => {
        const updatedArray = state.ProductDetailsInputs.filter((_, i) => i !== index);
        setState(prev => ({ ...prev, ProductDetailsInputs: updatedArray }));
    };

    const clearProductInputs = () => {
        setState(prev => ({
            ...prev,
            ProductTitle: '',
            ProductDetailsInputs: [{ data: '' }],
            ProductDescription: '',
            composition: '',
            manufacturer: '',
            tradeMark: '',
            patent: '',
            showSummaryPreviewTab: false,
            showSubmitTab: false,
            activeTab: 0
        }));
        setActiveStep(0);
        setSuccess(false);
    };

    const handleSubmit = async (e) => {
        if (!state.ProductTitle) {
            showToast("Warning", "Product title is required", "warning");
            return;
        }

        setLoading(true);

        const formDataToSend = new FormData();
        formDataToSend.append('uploadDate', getTodaysDate());
        formDataToSend.append('researcherName', state.userId);
        formDataToSend.append('universityName', state.userUniversity);
        formDataToSend.append('productTitle', state.ProductTitle);
        formDataToSend.append('productDescription', state.ProductDescription);
        formDataToSend.append('productDetails', JSON.stringify(state.ProductDetailsInputs));
        formDataToSend.append('composition', state.composition);
        formDataToSend.append('manufacturer', state.manufacturer);
        formDataToSend.append('tradeMark', state.tradeMark);
        formDataToSend.append('patent', state.patent);

        if (postStatus === "UPDATE" && editingProductId) {
            formDataToSend.append('updateId', editingProductId);
        }

        try {
            let postRequest;
            if (postStatus === "ADD") {
                postRequest = await axios.post(APIProductStore, formDataToSend, { headers });
            } else if (postStatus === "UPDATE") {
                postRequest = await axios.post(APIProductUpdate, formDataToSend, { headers });
            }

            const response = postRequest.data;
            if (response.status === "success") {
                clearProductInputs();
                
                const successMessage = postStatus === "ADD" 
                    ? "Product submitted successfully!" 
                    : "Product updated successfully!";
                    
                showToast("Success", successMessage, "success");
                setSuccess(true);
                
                setTimeout(() => navigate('/products'), 2000);
            } else {
                showToast("Error", "Unknown Server Error, Data Not Posted", "error");
            }

        } catch (error) {
            if (error.response) {
                if (error.response.status === 422) {
                    const errors = error.response.data.errors;
                    let errorMessages = [];
                    for (const field in errors) {
                        errorMessages.push(...errors[field]);
                    }
                    showToast("Validation Error", errorMessages.join("\n"), "error");
                } else {
                    showToast("Error", error.response.data.message || "An error occurred", "error");
                }
            } else if (error.code === "ERR_NETWORK") {
                showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
            } else {
                showToast("Error", "An unexpected error occurred", "error");
            }
        } finally {
            setLoading(false);
        }
    };

    const renderBasicInformation = () => (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Card sx={{ mb: 3, borderRadius: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon color="secondary" />
                            Product Basic Information
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        
                        <TextField
                            fullWidth
                            label="Product Title *"
                            name="ProductTitle"
                            value={state.ProductTitle}
                            onChange={handleInputChange}
                            margin="normal"
                            required
                            variant="outlined"
                        />
                        
                        <TextField
                            fullWidth
                            label="Product Description"
                            name="ProductDescription"
                            value={state.ProductDescription}
                            onChange={handleInputChange}
                            margin="normal"
                            multiline
                            rows={4}
                            placeholder="Provide a detailed description of your product..."
                        />
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    const renderProductDetails = () => (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Card sx={{ mb: 3, borderRadius: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ScienceIcon color="secondary" />
                            Product Specifications
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        
                        <Typography variant="subtitle1" gutterBottom>
                            Product Details / Features
                        </Typography>
                        {state.ProductDetailsInputs.map((item, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Enter product detail or feature"
                                    value={item.data}
                                    onChange={(e) => handleArrayInputChange(index, e.target.value)}
                                    sx={{ mr: 1 }}
                                />
                                {state.ProductDetailsInputs.length > 1 && (
                                    <Tooltip title="Remove">
                                        <IconButton 
                                            color="error" 
                                            onClick={() => removeInputField(index)}
                                        >
                                            <RemoveIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        ))}
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={addInputField}
                            sx={{ mt: 1 }}
                        >
                            Add Product Detail
                        </Button>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    const renderTechnicalSpecifications = () => (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Card sx={{ mb: 3, borderRadius: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <VerifiedIcon color="secondary" />
                            Technical & Legal Information
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        
                        <TextField
                            fullWidth
                            label="Composition"
                            name="composition"
                            value={state.composition}
                            onChange={handleInputChange}
                            margin="normal"
                            multiline
                            rows={3}
                            placeholder="Describe the product composition, ingredients, or materials..."
                            InputProps={{
                                startAdornment: <ScienceIcon color="secondary" sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                        
                        <TextField
                            fullWidth
                            label="Manufacturer"
                            name="manufacturer"
                            value={state.manufacturer}
                            onChange={handleInputChange}
                            margin="normal"
                            placeholder="Enter manufacturer name and details..."
                            InputProps={{
                                startAdornment: <FactoryIcon color="secondary" sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                        
                        <TextField
                            fullWidth
                            label="Trade Mark"
                            name="tradeMark"
                            value={state.tradeMark}
                            onChange={handleInputChange}
                            margin="normal"
                            placeholder="Enter trademark information..."
                            InputProps={{
                                startAdornment: <TramRounded color="secondary" sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                        
                        <TextField
                            fullWidth
                            label="Patent Information"
                            name="patent"
                            value={state.patent}
                            onChange={handleInputChange}
                            margin="normal"
                            multiline
                            rows={2}
                            placeholder="Enter patent numbers, filing dates, or status..."
                            InputProps={{
                                startAdornment: <VerifiedIcon color="secondary" sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    const renderPreview = () => (
        <Card sx={{ borderRadius: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ArticleIcon color="secondary" />
                    Product Preview
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Box sx={{ p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Author
                    </Typography>
                    <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {state.userName}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Product Title
                    </Typography>
                    <Typography variant="h5" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                        {state.ProductTitle}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Description
                    </Typography>
                    <Typography variant="body1" paragraph>
                        {state.ProductDescription || 'No description provided'}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Product Details / Features
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                        {state.ProductDetailsInputs.map((item, index) => (
                            item.data && (
                                <Typography key={index} variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                    • {item.data}
                                </Typography>
                            )
                        ))}
                    </Box>
                    
                    {(state.composition || state.manufacturer || state.tradeMark || state.patent) && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                Technical Specifications
                            </Typography>
                            
                            {state.composition && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="primary">
                                        Composition
                                    </Typography>
                                    <Typography variant="body2">{state.composition}</Typography>
                                </Box>
                            )}
                            
                            {state.manufacturer && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="primary">
                                        Manufacturer
                                    </Typography>
                                    <Typography variant="body2">{state.manufacturer}</Typography>
                                </Box>
                            )}
                            
                            {state.tradeMark && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="primary">
                                        Trade Mark
                                    </Typography>
                                    <Typography variant="body2">{state.tradeMark}</Typography>
                                </Box>
                            )}
                            
                            {state.patent && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="primary">
                                        Patent Information
                                    </Typography>
                                    <Typography variant="body2">{state.patent}</Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </CardContent>
        </Card>
    );

    const renderSubmission = () => (
        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Submission Progress
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={
                                        (state.ProductTitle ? 15 : 0) +
                                        (state.ProductDescription ? 10 : 0) +
                                        (state.ProductDetailsInputs.filter(i => i.data).length > 0 ? 25 : 0) +
                                        (state.composition ? 10 : 0) +
                                        (state.manufacturer ? 10 : 0) +
                                        (state.tradeMark ? 10 : 0) +
                                        (state.patent ? 10 : 0) +
                                        (state.userLoggedIn ? 10 : 0)
                                    } 
                                    sx={{ height: 8, borderRadius: 4 }}
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {Math.min(100,
                                    (state.ProductTitle ? 15 : 0) +
                                    (state.ProductDescription ? 10 : 0) +
                                    (state.ProductDetailsInputs.filter(i => i.data).length > 0 ? 25 : 0) +
                                    (state.composition ? 10 : 0) +
                                    (state.manufacturer ? 10 : 0) +
                                    (state.tradeMark ? 10 : 0) +
                                    (state.patent ? 10 : 0) +
                                    (state.userLoggedIn ? 10 : 0)
                                )}%
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            All required information has been completed. You're ready to {postStatus === 'UPDATE' ? 'update' : 'submit'} your product.
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {postStatus === 'UPDATE' ? 'Update Product' : 'Submit Product'}
                        </Typography>
                        {state.userLoggedIn ? (
                            <>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color={postStatus === 'UPDATE' ? 'warning' : 'secondary'}
                                    size="large"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    startIcon={
                                        loading ? <CircularProgress size={24} /> : 
                                        postStatus === 'UPDATE' ? <EditIcon /> : <CheckCircleIcon />
                                    }
                                    sx={{ mb: 2 }}
                                >
                                    {loading ? (postStatus === 'UPDATE' ? 'Updating...' : 'Submitting...') : 
                                     (postStatus === 'UPDATE' ? 'Update Product' : 'Submit Product')}
                                </Button>
                                {success && (
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="success"
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={() => navigate("/products")}
                                    >
                                        View All Products
                                    </Button>
                                )}
                            </>
                        ) : (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                You need to be logged in to {postStatus === 'UPDATE' ? 'update' : 'submit'} a product.
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    const renderGuidedForm = () => (
        <Box sx={{ maxWidth: '100%' }}>
            <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 4 }}>
                {steps.map((step, index) => (
                    <Step key={step.label}>
                        <StepLabel StepIconComponent={() => step.icon}>
                            <Typography variant="h6">{step.label}</Typography>
                        </StepLabel>
                        <StepContent>
                            <Box sx={{ mb: 2 }}>
                                {index === 0 && renderBasicInformation()}
                                {index === 1 && renderProductDetails()}
                                {index === 2 && renderTechnicalSpecifications()}
                                {index === 3 && renderPreview()}
                                
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                                    <Button
                                        disabled={index === 0}
                                        onClick={handlePrevStep}
                                        variant="outlined"
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={index === steps.length - 1 ? () => setState(prev => ({ ...prev, activeTab: 2 })) : handleNextStep}
                                        color="secondary"
                                    >
                                        {index === steps.length - 1 ? 'Review & Submit' : 'Continue'}
                                    </Button>
                                </Box>
                            </Box>
                        </StepContent>
                    </Step>
                ))}
            </Stepper>
        </Box>
    );

    if (loading) {
        return (
            <>
                <NavigationMenu />
                <LoadingState/>
            </>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <NavigationMenu />
            <Container Container maxWidth="lg" sx={{ py: 3, flex: 1, marginTop: 8 }}>
                <Slide direction="down" in={true} mountOnEnter unmountOnExit>
                    <Box sx={{
                        mb: 4,
                        p: 3,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                        color: 'white',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}>
                        <Box>
                            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {postStatus === 'UPDATE' ? 'Edit Research Product' : 'Submit New Research Product'}
                            </Typography>
                            <Typography variant="subtitle1">
                                {postStatus === 'UPDATE' ? 'Update your product details' : 'Share your research product with the community'}
                            </Typography>
                            {postStatus === 'UPDATE' && editingProductId && (
                                <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                                    Editing Product ID: {editingProductId}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                component={Link}
                                to="/products"
                                variant="contained"
                                sx={{
                                    bgcolor: 'white',
                                    color: theme.palette.secondary.main,
                                    fontWeight: 'bold',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.9)',
                                        transform: 'translateY(-2px)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                                startIcon={<ArticleIcon />}
                            >
                                View Products
                            </Button>
                        </Box>
                    </Box>
                </Slide>

                <Paper elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
                    <Tabs
                        value={state.activeTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{
                            '& .MuiTab-root': {
                                minHeight: 64,
                                fontSize: '1rem',
                                '&.Mui-selected': {
                                    color: theme.palette.secondary.main,
                                    fontWeight: 'bold'
                                }
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: theme.palette.secondary.main,
                                height: 3
                            }
                        }}
                    >
                        <Tab label="Product Form" />
                        <Tab label="Preview" disabled={!state.showSummaryPreviewTab} />
                        <Tab label={postStatus === 'UPDATE' ? 'Update' : 'Submit'} disabled={!state.showSubmitTab} />
                    </Tabs>
                </Paper>

                {state.activeTab === 0 && renderGuidedForm()}
                {state.activeTab === 1 && renderPreview()}
                {state.activeTab === 2 && renderSubmission()}
            </Container>
        </Box>
    );
};

export default SubmitProduct;